const COOKIE_NAME = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
const CONTROL_SELECTOR = '[data-halo-checkout], button[name="checkout"], input[name="checkout"], a[href]';
const RESTRICTED_REFERRER_NAME = /referrer|referer/i;
const RESTRICTED_ATTRIBUTION_NAME = /^(?:_orig_referrer|_fbp|_fbc|utm(?:[_-].*)?|gclid|dclid|fbclid|ttclid|msclkid|wbraid|gbraid|click(?:[_-]?id)?|campaign(?:[_-].*)?|ad(?:[_-]?(?:id|set|group|source))?|affiliate(?:[_-].*)?|landing(?:[_-]?(?:page|url|uri))?|source(?:[_-]?(?:page|url|uri))|client(?:[_-]?ip)|ip(?:[_-]?address)?|user(?:[_-]?agent)|ua)$/i;
const RESTRICTED_ATTRIBUTION_VALUE = /(?:^|[?&#;\s])(?:utm(?:[_-][a-z0-9_-]+)?|gclid|dclid|fbclid|ttclid|msclkid|wbraid|gbraid|_fbp|_fbc|click[_-]?id|landing[_-]?(?:page|url|uri)|source[_-]?(?:page|url|uri))\s*[:=]/i;
const KNOWN_RESTRICTED_NAMES = Object.freeze([
  'referrer', 'referer', '_orig_referrer',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id',
  'gclid', 'dclid', 'fbclid', 'ttclid', 'msclkid', 'wbraid', 'gbraid',
]);

function decodePercentLayer(value) {
  return String(value).replace(/\+/g, ' ').replace(/%([0-9a-f]{2})/gi, (_match, hex) =>
    String.fromCharCode(Number.parseInt(hex, 16)));
}

function decodedTextCandidates(value) {
  const candidates = [];
  let current = String(value ?? '');
  // Every effective percent-decoding pass shortens the input. Using the input
  // length as the budget reaches a fixed point without an arbitrary depth that
  // an attacker could exceed. Invalid escapes are left untouched while valid
  // escapes around them continue to decode.
  for (let remaining = current.length + 1; remaining > 0; remaining -= 1) {
    candidates.push(current);
    const decoded = decodePercentLayer(current);
    if (decoded === current) return candidates;
    current = decoded;
  }
  candidates.push(current);
  return candidates;
}

function cookieDomainCandidates(configuredDomain, hostname) {
  const domains = new Set(['']);
  const host = String(hostname || '').trim().toLowerCase().replace(/^\.+|\.+$/g, '');
  const configured = String(configuredDomain || '').trim().toLowerCase().replace(/^\.+|\.+$/g, '');
  const add = (domain) => {
    if (!domain || !/^[a-z0-9.-]+$/.test(domain)) return;
    domains.add(domain);
    domains.add(`.${domain}`);
  };

  if (configured && (host === configured || host.endsWith(`.${configured}`))) add(configured);
  const labels = host.split('.').filter(Boolean);
  for (let index = 0; index < labels.length - 1; index += 1) {
    add(labels.slice(index).join('.'));
  }
  return [...domains];
}

function expireCookieEverywhere(name, configuredDomain = '') {
  if (!COOKIE_NAME.test(name)) return;
  const paths = ['/', '/cart', '/cart/', '/checkout'];
  const domains = cookieDomainCandidates(configuredDomain, window.location.hostname);
  for (const path of paths) {
    for (const domain of domains) {
      const scope = `Path=${path}${domain ? `; Domain=${domain}` : ''}`;
      document.cookie = `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; ${scope}; SameSite=Lax; Secure`;
    }
  }
}

function nestedContainsRestricted(value, depth = 0) {
  // Deep structured values have no supported business purpose in line-item
  // properties. Fail closed instead of letting nesting bypass the rule.
  if (depth > 6) return true;
  if (typeof value === 'string') return containsRestrictedReferrer(value, false);
  if (Array.isArray(value)) return value.some((item) => nestedContainsRestricted(item, depth + 1));
  if (value && typeof value === 'object') {
    return Object.entries(value).some(([key, item]) =>
      isRestrictedTransferName(key) || nestedContainsRestricted(item, depth + 1));
  }
  return false;
}

export function isRestrictedTransferName(value) {
  return decodedTextCandidates(value).some((candidate) =>
    RESTRICTED_REFERRER_NAME.test(candidate) || RESTRICTED_ATTRIBUTION_NAME.test(candidate.trim()));
}

export function containsRestrictedReferrer(value, inspectJson = true) {
  return decodedTextCandidates(value).some((candidate) => {
    if (RESTRICTED_REFERRER_NAME.test(candidate) || RESTRICTED_ATTRIBUTION_VALUE.test(candidate)) {
      return true;
    }
    if (!inspectJson || !/^[\[{]/.test(candidate.trim())) return false;
    try {
      return nestedContainsRestricted(JSON.parse(candidate));
    } catch {
      return false;
    }
  });
}

  function sanitizeCurrentPageUrl() {
    // Preserve the current URL authorized for the direct Store 1 Meta events.
    // Never read document.referrer or inspect cookie/storage values here.
    try {
      var currentUrl = new URL(window.location.href);
      Array.from(currentUrl.searchParams.keys()).forEach(function (key) {
        var decodedKey = String(key);
        for (var depth = 0; depth < key.length + 1; depth += 1) {
          var next = decodedKey.replace(/%([0-9a-f]{2})/gi, function (_match, hex) {
            return String.fromCharCode(parseInt(hex, 16));
          });
          if (next === decodedKey) break;
          decodedKey = next;
        }
        if (/referr?er|(?:^|[_-])(?:source|landing)[_-]?(?:url|page|uri)(?:$|[_-])/i.test(decodedKey)) {
          currentUrl.searchParams.delete(key);
        }
      });
      currentUrl.hash = '';
      window.history.replaceState(null, '', currentUrl.pathname + currentUrl.search);
    } catch (_error) {}
  }

export function purgeRestrictedReferrerArtifacts(cookieDomain = '') {
  // Clear browser storage without enumerating or reading any stored key/value.
  for (const storage of [window.localStorage, window.sessionStorage]) {
    try {
      storage.clear();
    } catch {
      // Storage can be unavailable in restricted browsing contexts.
    }
  }

  // Delete only known names, blindly. Never read document.cookie.
  try {
    for (const knownName of KNOWN_RESTRICTED_NAMES) expireCookieEverywhere(knownName, cookieDomain);
  } catch {
    // Cookie deletion is best effort; no cookie values are inspected.
  }

  window.name = '';
  sanitizeCurrentPageUrl();
}

export function sanitizeProperties(properties, allowedKeys = new Set()) {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return {};
  const sanitized = {};
  for (const key of allowedKeys) {
    if (Object.keys(sanitized).length >= 50) break;
    if (isRestrictedTransferName(key) || !Object.hasOwn(properties, key)) continue;
    const value = properties[key];
    if (typeof value !== 'string' || value === '' || containsRestrictedReferrer(value) ||
        /(?:https?:\/\/|\/\/[^\s/]+\.)/i.test(value)) continue;
    sanitized[key] = value;
  }
  return sanitized;
}

function cartPricing(cart) {
  return {
    originalTotalPriceCents: Number(cart.original_total_price),
    finalTotalPriceCents: Number(cart.total_price),
    itemsSubtotalPriceCents: Number(cart.items_subtotal_price),
    totalDiscountCents: Number(cart.total_discount),
  };
}

function itemPricing(item) {
  return {
    originalUnitPriceCents: Number(item.original_price),
    finalUnitPriceCents: Number(item.final_price),
    originalLinePriceCents: Number(item.original_line_price),
    finalLinePriceCents: Number(item.final_line_price),
  };
}

export function cartToResolvePayload(
  cart,
  allowedPropertyKeys = new Set(),
  disclosureVersion = '',
  disclosureAccepted = false,
) {
  if (!cart || !Array.isArray(cart.items)) throw new Error('INVALID_CART');
  return {
    currency: String(cart.currency ?? '').toUpperCase(),
    disclosure: { accepted: disclosureAccepted === true, version: disclosureVersion },
    cartPricing: cartPricing(cart),
    items: cart.items.map((item) => ({
      sourceVariantId: String(item.variant_id ?? item.id ?? ''),
      quantity: Number(item.quantity),
      pricing: itemPricing(item),
      properties: sanitizeProperties(item.properties, allowedPropertyKeys),
    })),
  };
}

export function validateTransferUrl(value, allowedHosts, transferPath = '/pages/halo-transfer') {
  const parsed = new URL(value);
  const hosts = new Set(allowedHosts.map((host) => host.trim().toLowerCase()).filter(Boolean));
  const fragment = /^#transfer=(v1\.[A-Za-z0-9_-]{1,32}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)$/.exec(parsed.hash);
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password ||
      (parsed.port && parsed.port !== '443') || !hosts.has(parsed.hostname.toLowerCase()) ||
      parsed.pathname.replace(/\/$/, '') !== transferPath.replace(/\/$/, '') || parsed.search ||
      !fragment || fragment[1].length > 50_000) {
    throw new Error('INVALID_TRANSFER_URL');
  }
  return parsed.toString();
}

function validatePreparedCheckoutUrl(value, allowedHosts) {
  const parsed = new URL(value);
  const hosts = new Set(allowedHosts.map((host) => host.trim().toLowerCase()).filter(Boolean));
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password ||
      (parsed.port && parsed.port !== '443') || !hosts.has(parsed.hostname.toLowerCase()) ||
      !/^\/(?:checkouts\/|cart\/c\/)/.test(parsed.pathname) || parsed.hash) {
    throw new Error('INVALID_PREPARED_CHECKOUT_URL');
  }
  return parsed.toString();
}

function validateBackendEndpoint(value, expectedPath) {
  const parsed = new URL(value);
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password ||
      (parsed.port && parsed.port !== '443') || parsed.search || parsed.hash ||
      parsed.pathname !== expectedPath) {
    throw new Error('INVALID_BACKEND_ENDPOINT');
  }
  return parsed.toString();
}

function anchorPointsToCheckout(element) {
  if (element?.tagName !== 'A') return false;
  try {
    const url = new URL(element.getAttribute('href'), window.location.origin);
    return url.origin === window.location.origin &&
      /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout\/?$/i.test(url.pathname);
  } catch {
    return false;
  }
}

function isCheckoutControl(element) {
  return Boolean(element?.matches?.(
    '[data-halo-checkout], button[name="checkout"], input[name="checkout"]',
  ) || anchorPointsToCheckout(element));
}

function findDisclosureControl(control) {
  const scope = control?.closest?.('.m-cart__checkout, .m-cart-drawer__inner, form');
  const scoped = scope?.querySelector?.('[data-halo-plain-product-disclosure]');
  if (scoped) return scoped;
  if (typeof document === 'undefined') return null;
  return document.querySelector('[data-halo-plain-product-disclosure]');
}

export function isDisclosureAcknowledged(control, disclosureVersion) {
  const checkbox = findDisclosureControl(control);
  return Boolean(
    checkbox && checkbox.type === 'checkbox' && checkbox.checked === true &&
    checkbox.dataset?.disclosureVersion === disclosureVersion,
  );
}

function isNativeCheckoutForm(form) {
  if (!form?.matches?.('form')) return false;
  try {
    const declaredAction = form.getAttribute('action');
    if (!declaredAction) return false;
    const action = new URL(declaredAction, window.location.origin);
    const cartAction = action.origin === window.location.origin &&
      /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\/?$/i.test(action.pathname);
    return cartAction && Boolean(form.querySelector?.(
      '[data-halo-checkout], button[name="checkout"], input[name="checkout"]',
    ));
  } catch {
    return false;
  }
}

function showError(message) {
  let region = document.querySelector('[data-halo-checkout-error]');
  if (!region) {
    region = document.createElement('p');
    region.dataset.haloCheckoutError = '';
    region.setAttribute('role', 'alert');
    region.setAttribute('aria-live', 'assertive');
    region.style.color = '#b42318';
    region.style.margin = '12px 0';
    (document.querySelector('form[action*="/cart"]') ?? document.body).append(region);
  }
  region.textContent = message;
}

function setCheckoutDisabled(disabled) {
  document.querySelectorAll(CONTROL_SELECTOR).forEach((control) => {
    if (!isCheckoutControl(control)) return;
    if ('disabled' in control) control.disabled = disabled;
    control.setAttribute('aria-disabled', String(disabled));
  });
}

function hasValidPricing(payload) {
  const values = [
    ...Object.values(payload.cartPricing ?? {}),
    ...payload.items.flatMap((item) => Object.values(item.pricing ?? {})),
  ];
  return values.length === 4 + (payload.items.length * 4) &&
    values.every((value) => Number.isSafeInteger(value) && value >= 0);
}

function navigateNoReferrer(url) {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_self';
  link.rel = 'noreferrer noopener';
  link.referrerPolicy = 'no-referrer';
  link.hidden = true;
  document.body.append(link);
  link.click();
}

export function installCheckoutBridge({
  endpoint,
  allowedHosts,
  transferPath = '/pages/halo-transfer',
  cookieDomain = '',
  allowedPropertyKeys = new Set(),
  disclosureVersion = '',
  fetchImpl = window.fetch.bind(window),
}) {
  let resolving = false;
  let prepared = null;
  let preparing = null;
  let warmTimer = null;
  let warmFrame = null;

  function resetCheckoutNavigationState() {
    resolving = false;
    setCheckoutDisabled(false);
  }


  function discardWarmFrame() {
    if (warmFrame?.frame?.remove) warmFrame.frame.remove();
    warmFrame = null;
  }

  function precreateStore2Cart(record) {
    discardWarmFrame();
    const frame = document.createElement('iframe');
    frame.hidden = true;
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    frame.referrerPolicy = 'no-referrer';
    frame.src = record.url;
    warmFrame = { frame, record };
    (document.body ?? document.documentElement).append(frame);
  }

  function acceptPreparedCheckout(event) {
    if (!warmFrame || event.source !== warmFrame.frame.contentWindow ||
        event.data?.type !== 'halo-checkout-ready') return;
    const expectedOrigins = new Set(
      allowedHosts.map((host) => host.trim().toLowerCase()).filter(Boolean)
        .map((host) => `https://${host}`),
    );
    if (!expectedOrigins.has(String(event.origin).toLowerCase())) return;
    try {
      const checkoutUrl = validatePreparedCheckoutUrl(event.data.checkoutUrl, allowedHosts);
      if (prepared !== warmFrame.record || prepared.expiresAt <= Date.now()) return;
      prepared.checkoutUrl = checkoutUrl;
      discardWarmFrame();
    } catch {
      // Ignore malformed or stale cross-origin responses and keep the normal path.
    }
  }

  window.addEventListener('message', acceptPreparedCheckout);
    // Browsers may restore Store 1 from the back-forward cache after the customer
  // returns from Store 2. Reset the in-memory lock and the persisted disabled
  // controls both before leaving and whenever the page is shown again.
  window.addEventListener('pagehide', resetCheckoutNavigationState);
  window.addEventListener('pageshow', resetCheckoutNavigationState);


  async function readPayload() {
    purgeRestrictedReferrerArtifacts(cookieDomain);
    if (!endpoint || allowedHosts.length === 0) throw new Error('BRIDGE_NOT_CONFIGURED');
    const cartResponse = await fetchImpl(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      redirect: 'error',
      referrerPolicy: 'no-referrer',
    });
    if (!cartResponse.ok) throw new Error('CART_READ_FAILED');
    const payload = cartToResolvePayload(
      await cartResponse.json(),
      allowedPropertyKeys,
      disclosureVersion,
      true,
    );
    if (payload.currency !== 'USD' || payload.items.length === 0 || !hasValidPricing(payload)) {
      throw new Error('INVALID_CART');
    }
    return payload;
  }

  async function preparePayload(payload) {
    const fingerprint = JSON.stringify(payload);
    if (prepared?.fingerprint === fingerprint && prepared.expiresAt > Date.now()) {
      return prepared;
    }
    if (preparing?.fingerprint === fingerprint) return preparing.promise;
    const promise = (async () => {
      const prepareEndpoint = validateBackendEndpoint(endpoint, '/api/checkout/prepare');
      const resolveResponse = await fetchImpl(prepareEndpoint, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: fingerprint,
        cache: 'no-store',
        redirect: 'error',
        referrerPolicy: 'no-referrer',
      });
      if (!resolveResponse.ok) throw new Error('CHECKOUT_PREPARE_FAILED');
      const result = await resolveResponse.json();
      const url = validateTransferUrl(result.transferUrl, allowedHosts, transferPath);
      const checkoutUrl = result.checkoutUrl
        ? validatePreparedCheckoutUrl(result.checkoutUrl, allowedHosts)
        : '';
      prepared = { fingerprint, url, checkoutUrl, expiresAt: Date.now() + 480_000 };
      if (!checkoutUrl) precreateStore2Cart(prepared);
      return prepared;
    })();
    preparing = { fingerprint, promise };
    try {
      return await promise;
    } finally {
      if (preparing?.promise === promise) preparing = null;
    }
  }

  async function prepareCurrentCart() {
    return preparePayload(await readPayload());
  }

  function scheduleWarm() {
    if (resolving) return;
    if (warmTimer !== null) window.clearTimeout(warmTimer);
    warmTimer = window.setTimeout(() => {
      warmTimer = null;
      void prepareCurrentCart().catch(() => {});
    }, 120);
  }

  async function begin(event) {
    if (resolving) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    resolving = true;
    setCheckoutDisabled(true);
    try {
      const checkout = await prepareCurrentCart();
      purgeRestrictedReferrerArtifacts(cookieDomain);
      navigateNoReferrer(checkout.checkoutUrl || checkout.url);
    } catch {
      // Fail closed: there is intentionally no fallback to Store 1 checkout.
      resolving = false;
      setCheckoutDisabled(false);
      showError('Não foi possível preparar o pagamento com segurança. Atualize a página e tente novamente.');
    }
  }

  document.addEventListener('click', (event) => {
    const control = event.target.closest?.(CONTROL_SELECTOR);
    if (isCheckoutControl(control)) begin(event);
  }, true);

  document.addEventListener('submit', (event) => {
    if (isCheckoutControl(event.submitter) || isNativeCheckoutForm(event.target)) begin(event);
  }, true);

  ['pointerenter', 'focusin', 'touchstart'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const control = event.target.closest?.(CONTROL_SELECTOR);
      if (isCheckoutControl(control)) scheduleWarm();
    }, true);
  });

  let automaticWarmPending = false;

  function containsCheckoutControl(node) {
    return isCheckoutControl(node) || Array.from(node.querySelectorAll?.(CONTROL_SELECTOR) ?? [])
      .some((control) => isCheckoutControl(control));
  }

  function hasVisibleCheckoutControl() {
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    return Array.from(document.querySelectorAll(CONTROL_SELECTOR)).some((control) => {
      if (!isCheckoutControl(control) || !control.getClientRects?.().length) return false;
      const drawer = control.closest?.('.m-cart-drawer');
      if (drawer && !drawer.classList.contains('m-cart-drawer--active')) return false;
      const style = window.getComputedStyle(control);
      if (style.display === 'none' || style.visibility === 'hidden' ||
          style.visibility === 'collapse' || style.opacity === '0') return false;
      const rect = control.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 &&
        rect.top < viewportHeight && rect.left < viewportWidth;
    });
  }

  function scheduleAutomaticWarm() {
    if (automaticWarmPending || resolving || document.readyState !== 'complete' ||
        !hasVisibleCheckoutControl()) return;
    automaticWarmPending = true;
    const run = () => {
      automaticWarmPending = false;
      if (!resolving && document.readyState === 'complete' && hasVisibleCheckoutControl()) scheduleWarm();
    };
    if (typeof window.requestIdleCallback === 'function') {
      try {
        window.requestIdleCallback(run, { timeout: 1200 });
        return;
      } catch (_error) {}
    }
    window.setTimeout(run, 250);
  }

  const observer = new MutationObserver((mutations) => {
    const checkoutAdded = mutations.some(({ addedNodes }) => Array.from(addedNodes).some((node) =>
      node.nodeType === 1 && containsCheckoutControl(node),
    ));
    if (checkoutAdded) scheduleAutomaticWarm();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('load', scheduleAutomaticWarm, { once: true });
  scheduleAutomaticWarm();
  const warmupMarker = document.getElementById('halo-checkout-bridge');
  if (warmupMarker) warmupMarker.dataset.haloWarmupScheduling = 'load-idle-visible-v1';
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  const script = document.getElementById('halo-checkout-bridge');
  if (script) {
    installCheckoutBridge({
      endpoint: script.dataset.endpoint ?? '',
      allowedHosts: (script.dataset.allowedTransferHosts ?? '').split(','),
      transferPath: script.dataset.transferPath ?? '/pages/halo-transfer',
      cookieDomain: script.dataset.cookieDomain ?? '',
      allowedPropertyKeys: new Set(
        (script.dataset.allowedPropertyKeys ?? '').split(',').map((key) => key.trim()).filter(Boolean),
      ),
      disclosureVersion: script.dataset.disclosureVersion ?? '',
    });
  }
}
